/**
 * 커스텀 폰트 로더
 * public/editor_font/fonts.json 매니페스트를 읽어 FontFace API로 등록
 *
 * fonts.json 형식:
 * [
 *   {
 *     "family": "맑은 고딕",
 *     "files": {
 *       "normal": "malgun.ttf",
 *       "bold": "malgunbd.ttf"
 *     }
 *   }
 * ]
 *
 * 새 폰트 추가 시:
 * 1. public/editor_font/ 에 .ttf/.otf/.woff2 파일 추가
 * 2. fonts.json에 항목 추가
 * → 자동으로 에디터 폰트 목록에 반영
 */

const FONT_BASE_PATH = '/editor_font';

/**
 * 폰트 매니페스트를 로드하고 FontFace API로 브라우저에 등록
 * @returns {Promise<Array<{ value: string, label: string }>>} 로드된 폰트 목록
 */
export const loadCustomFonts = async () => {
  try {
    const res = await fetch(`${FONT_BASE_PATH}/fonts.json`);
    if (!res.ok) return [];

    const manifest = await res.json();
    const loaded = [];

    for (const entry of manifest) {
      const { family, files } = entry;
      if (!family || !files) continue;

      const weights = [];

      // normal (400)
      if (files.normal) {
        weights.push({ weight: 'normal', file: files.normal });
      }
      // bold (700)
      if (files.bold) {
        weights.push({ weight: 'bold', file: files.bold });
      }
      // 추가 weight 지원 (light, medium 등)
      const extraWeights = { light: '300', medium: '500', semibold: '600', black: '900' };
      for (const [key, weightValue] of Object.entries(extraWeights)) {
        if (files[key]) {
          weights.push({ weight: weightValue, file: files[key] });
        }
      }

      let familyLoaded = false;

      for (const { weight, file } of weights) {
        try {
          const url = `url(${FONT_BASE_PATH}/${file})`;
          const fontFace = new FontFace(family, url, { weight });
          await fontFace.load();
          document.fonts.add(fontFace);
          familyLoaded = true;
        } catch (err) {
          console.warn(`폰트 로드 실패: ${family} (${weight})`, err);
        }
      }

      if (familyLoaded) {
        loaded.push({ value: family, label: family });
      }
    }

    return loaded;
  } catch (err) {
    console.warn('폰트 매니페스트 로드 실패:', err);
    return [];
  }
};
